import React, { useContext, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  Pagination,
  Snackbar,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";

import Store from "../../../Store/Store";
import CTABlock from "../../../components/CTABlock";
import SpecSpinner from "../../../components/SpecSpinner";
import SpecBackButton from "../../../components/SpecBackButton";
import inventoryClient from "../../../clients/inventoryClient";
import useServingSizes from "../../../hooks/useServingSizes";
import ServingSizeCard from "./ServingSizeCard";
import CreateEditServingSizeModal from "../../../components/Modals/CreateEditServingSizeModal";
import DeleteServingSizeModal from "../../../components/Modals/DeleteServingSizeModal";

const ServingSizes = () => {
  const [page, setPage] = useState(1);
  const [servingSizeModalOpen, toggleServingSizeModalOpen] = useState(false);
  const [deleteModalOpen, toggleDeleteModalOpen] = useState(false);
  const [selectedServingSize, setSelectedServingSize] = useState(undefined);
  const [alertSuccess, setAlertSuccess] = useState(undefined);
  const [errorMessage, setErrorMessage] = useState(undefined);

  const store = useContext(Store);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const establishmentId = store.currentEstablishment?.id;

  const {
    data: servingSizeData,
    isLoading,
    isError,
  } = useServingSizes(establishmentId, page);

  // The paginated list and the "all" list the pickers use are separate cache keys - react-query
  // matches key arrays element by element, so "serving-sizes" does not prefix-match
  // "serving-sizes-all".
  const refreshServingSizeLists = () => {
    queryClient.invalidateQueries({ queryKey: ["serving-sizes"] });
    queryClient.refetchQueries({ queryKey: ["serving-sizes"] });
    queryClient.invalidateQueries({ queryKey: ["serving-sizes-all"] });
    // the Organization hub shows a serving size count on its card
    queryClient.invalidateQueries({ queryKey: ["organization-count"] });
  };

  // A serving size's name and measurement are rendered on the families it is a default for and on
  // every format it is attached to, and deleting one detaches it from both. Without dropping these
  // too, navigating to Families or the Product Library keeps showing the old connection until a
  // hard refresh.
  const refreshServingSizeConnections = () => {
    refreshServingSizeLists();
    queryClient.invalidateQueries({ queryKey: ["productFamilyGroups"] });
    queryClient.invalidateQueries({ queryKey: ["inventory"] });
    queryClient.invalidateQueries({ queryKey: ["inventory-paginated"] });
    queryClient.invalidateQueries({ queryKey: ["paginated-product-search"] });
  };

  const handleOpenCreate = () => {
    setSelectedServingSize(undefined);
    toggleServingSizeModalOpen(true);
  };

  const handleOpenEdit = (servingSize) => {
    setSelectedServingSize(servingSize);
    toggleServingSizeModalOpen(true);
  };

  const handleOpenDelete = (servingSize) => {
    setSelectedServingSize(servingSize);
    toggleDeleteModalOpen(true);
  };

  const handleSave = async ({ name, unitSize }) => {
    try {
      if (selectedServingSize) {
        await inventoryClient.updateServingSize({
          establishmentId,
          servingSizeId: selectedServingSize.id,
          name,
          unitSize,
        });
        setAlertSuccess("Serving size updated");
        refreshServingSizeConnections();
      } else {
        await inventoryClient.addServingSize({
          establishmentId,
          name,
          unitSize,
        });
        setAlertSuccess("Serving size created");
        // nothing is attached to a brand new serving size yet, so the families and the product
        // library are still accurate
        refreshServingSizeLists();
      }
    } catch {
      setErrorMessage("There was an error saving the serving size");
    }
  };

  const handleDelete = async () => {
    try {
      await inventoryClient.deleteServingSize({
        establishmentId,
        servingSizeId: selectedServingSize.id,
      });
      setAlertSuccess("Serving size deleted");
      refreshServingSizeConnections();
    } catch {
      setErrorMessage("There was an error deleting the serving size");
    }
  };

  if (isLoading) {
    return <SpecSpinner open text="Loading serving sizes..." />;
  }

  const servingSizes = servingSizeData?.servingSizes ?? [];
  const totalPages = servingSizeData?.totalPages ?? 0;

  return (
    <>
      <Grid
        container
        sx={{
          maxWidth: "2440px",
          padding: 4,
          marginTop: 6,
        }}
      >
        {isError && (
          <Typography variant="h6" sx={{ color: "red" }}>
            There was an error loading serving sizes, please refresh
          </Typography>
        )}
        <Grid item sx={{ marginLeft: -2 }}>
          <SpecBackButton
            onClick={() => navigate(`/spec/${establishmentId}/organization`)}
            backText="Organization"
          />
        </Grid>
        <Grid
          container
          sx={{
            maxWidth: "2440px",
            marginBottom: 6,
          }}
        >
          <Grid item xs={9}>
            <Typography variant="h2">Serving Sizes</Typography>
          </Grid>
          <Grid
            item
            xs={3}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              sx={{
                borderRadius: 2,
                padding: 4,
                justifySelf: "flex-end",
                height: "40px",
              }}
              onClick={handleOpenCreate}
            >
              <AddOutlinedIcon
                sx={{ marginRight: 1, fontSize: { xs: 14, md: 20 } }}
              />
              <Typography
                variant="smallButton"
                sx={{
                  fontSize: { xs: "8px", md: "12px" },
                  lineHeight: { xs: 1 },
                }}
              >
                Add Serving Size
              </Typography>
            </Button>
          </Grid>
        </Grid>

        <Grid container sx={{ marginBottom: 2, width: "100%" }}>
          <Grid item xs={12}>
            {servingSizes.length > 0 ? (
              servingSizes.map((s) => (
                <ServingSizeCard
                  key={s.id}
                  servingSize={s}
                  onEdit={handleOpenEdit}
                  onDelete={handleOpenDelete}
                />
              ))
            ) : (
              <CTABlock
                header={"You haven't added any serving sizes"}
                subheader={
                  "Add serving sizes to see what a single pour or portion costs"
                }
                buttonText="Add Serving Size"
                handleCTA={handleOpenCreate}
              />
            )}
          </Grid>
        </Grid>

        {totalPages > 1 && (
          <Grid
            container
            sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, value) => setPage(value)}
            />
          </Grid>
        )}
      </Grid>

      <CreateEditServingSizeModal
        isOpen={servingSizeModalOpen}
        toggleModalOpen={toggleServingSizeModalOpen}
        servingSize={selectedServingSize}
        onSave={handleSave}
        onDelete={(servingSize) => {
          setSelectedServingSize(servingSize);
          toggleDeleteModalOpen(true);
        }}
      />
      <DeleteServingSizeModal
        isOpen={deleteModalOpen}
        toggleModalOpen={toggleDeleteModalOpen}
        servingSizeName={selectedServingSize?.name}
        handleDelete={handleDelete}
      />
      <Box>
        <Snackbar
          open={alertSuccess ? true : false}
          onClose={() => setAlertSuccess(undefined)}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity="success" variant="filled">
            {alertSuccess}
          </Alert>
        </Snackbar>
        <Snackbar
          open={errorMessage ? true : false}
          onClose={() => setErrorMessage(undefined)}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity="error" variant="filled">
            {errorMessage}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default ServingSizes;
